import React, { createContext, useContext, useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-hot-toast'
import Cookies from 'js-cookie'

import { adminAuthService } from '../services/authService'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const navigate = useNavigate()
  const location = useLocation()

  // Check if user is authenticated on app start
  useEffect(() => {
    checkAuthStatus()
  }, [])

  // Set up token refresh interval
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(() => {
        refreshToken()
      }, 15 * 60 * 1000) // Refresh every 15 minutes

      return () => clearInterval(interval)
    }
  }, [isAuthenticated])

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true)
      const token = Cookies.get('admin_token') || localStorage.getItem('admin_token')
      
      if (!token) {
        setIsLoading(false)
        return
      }

      // Verify token with backend
      const response = await adminAuthService.verifyToken()
      
      if (response.success) {
        setUser(response.user)
        setIsAuthenticated(true)
        
        // Redirect to intended page or dashboard
        const intendedPath = sessionStorage.getItem('intended_path')
        if (intendedPath && location.pathname === '/login') {
          sessionStorage.removeItem('intended_path')
          navigate(intendedPath, { replace: true })
        }
      } else {
        // Token is invalid, clear it
        Cookies.remove('admin_token')
        localStorage.removeItem('admin_token')
        setUser(null)
        setIsAuthenticated(false)
      }
    } catch (error) {
      console.error('Auth check error:', error)
      Cookies.remove('admin_token')
      localStorage.removeItem('admin_token')
      setUser(null)
      setIsAuthenticated(false)
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (credentials) => {
    try {
      setIsLoading(true)
      
      const response = await adminAuthService.login(credentials)
      
      if (response.success) {
        // Store token in both cookie and localStorage
        Cookies.set('admin_token', response.token, {
          expires: 7, // 7 days
          secure: import.meta.env.VITE_SECURE_COOKIES === 'true',
          sameSite: 'strict'
        })
        localStorage.setItem('admin_token', response.token)
        
        setUser(response.user)
        setIsAuthenticated(true)
        
        toast.success(`Hoş geldiniz, ${response.user.email}!`)
        
        // Redirect to intended page or dashboard
        const intendedPath = sessionStorage.getItem('intended_path') || '/dashboard'
        sessionStorage.removeItem('intended_path')
        navigate(intendedPath, { replace: true })
        
        return { success: true }
      } else {
        toast.error(response.message || 'Giriş başarısız')
        return { success: false, message: response.message }
      }
    } catch (error) {
      console.error('Login error:', error)
      const message = error.response?.data?.message || 'Giriş sırasında hata oluştu'
      toast.error(message)
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server
      await adminAuthService.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Clear local state regardless of server response
      Cookies.remove('admin_token')
      localStorage.removeItem('admin_token')
      setUser(null)
      setIsAuthenticated(false)
      
      toast.success('Başarıyla çıkış yaptınız')
      navigate('/login', { replace: true })
    }
  }

  const refreshToken = async () => {
    try {
      const response = await adminAuthService.refreshToken()
      
      if (response.success) {
        // Update token in cookie and localStorage
        Cookies.set('admin_token', response.token, {
          expires: 7,
          secure: import.meta.env.VITE_SECURE_COOKIES === 'true',
          sameSite: 'strict'
        })
        localStorage.setItem('admin_token', response.token)
        
        // Update user data if provided
        if (response.user) {
          setUser(response.user)
        }
        
        return true
      } else {
        // Refresh failed, force logout
        await logout()
        return false
      }
    } catch (error) {
      console.error('Token refresh error:', error)
      await logout()
      return false
    }
  }

  const updateUser = (userData) => {
    setUser(prevUser => ({
      ...prevUser,
      ...userData
    }))
  }

  // Save intended path when redirecting to login
  const saveIntendedPath = (path) => {
    if (path !== '/login') {
      sessionStorage.setItem('intended_path', path)
    }
  }

  const contextValue = {
    // State
    user,
    isAuthenticated,
    isLoading,
    
    // Actions
    login,
    logout,
    refreshToken,
    updateUser,
    saveIntendedPath,
    checkAuthStatus,
    
    // User Properties (for convenience)
    isAdmin: user?.role === 'admin',
    isSuperAdmin: user?.role === 'super_admin',
    userName: user?.email || user?.username || 'Admin',
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}
